import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useState } from 'react';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';
import { OfficialChannelsFollowBlock, type OfficialChannel } from './OfficialChannelsFollowBlock';

interface JoinLeagueModalProps {
  leagueName: string;
  /** Official channels of the league's competition. Hidden when empty or posts are disabled. */
  officialChannels: OfficialChannel[];
  /** `officialChannelIds` is undefined when there are no official channels to follow. */
  onJoin: (officialChannelIds?: string[]) => Promise<void>;
  onDismiss: () => void;
  isLoading: boolean;
  error: string | null;
}

/** Confirmation screen to join a public league. */
export const JoinLeagueModal = ({
  leagueName,
  officialChannels,
  onJoin,
  onDismiss,
  isLoading,
  error,
}: JoinLeagueModalProps) => {
  const { isEnabled } = useFeatureFlags();
  const [selectedOfficialChannelIds, setSelectedOfficialChannelIds] = useState(() =>
    officialChannels.map((channel) => channel.id)
  );
  const showOfficialChannels = isEnabled(FeatureFlag.POSTS) && officialChannels.length > 0;

  const handleJoin = async () => {
    await onJoin(showOfficialChannels ? selectedOfficialChannelIds : undefined);
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Unirse a la Liga</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss} disabled={isLoading}>
              Cancelar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="space-y-4">
          <IonText className="mb-4 block">
            <h2 className="text-lg font-semibold">{leagueName}</h2>
            <p className="text-sm text-gray-600 mt-2">
              ¿Estás seguro de que quieres unirte a &quot;{leagueName}&quot;?
            </p>
          </IonText>

          {showOfficialChannels && (
            <OfficialChannelsFollowBlock
              action="join"
              channels={officialChannels}
              selectedChannelIds={selectedOfficialChannelIds}
              onSelectedChannelIdsChange={setSelectedOfficialChannelIds}
            />
          )}

          {error && (
            <IonText color="danger">
              <p className="text-sm">{error}</p>
            </IonText>
          )}

          <IonButton expand="block" onClick={handleJoin} disabled={isLoading}>
            {isLoading ? (
              <>
                <IonSpinner name="crescent" />
                <span className="ml-2">Uniéndose...</span>
              </>
            ) : (
              'Unirme'
            )}
          </IonButton>
        </div>
      </IonContent>
    </>
  );
};
